<template>
	<div class="card">
		<h4>Documents</h4>
		<TabView v-model:activeIndex="activeDocumentPanelTab">
			<TabPanel>
				<template #header>
					<i class="pi pi-file"></i>&nbsp;
					<span>Docs</span>
				</template>
				<div style="margin-bottom:10px;" class="grid ml-0 mr-1">
					<div class="col" style="min-width:200px;">
						<div class="flex flex-auto p-input-icon-left p-input-icon-right">
							<i class="pi pi-home" />
							<InputText v-model="filterText" placeholder="Document keyword filter.." />
							<i class="pi pi-search" />
						</div>
					</div>
					<div class="col-fixed">
						<div>
							<span>Since: </span>
							<Calendar v-model="startDate" :showIcon="true" dateFormat="d M yy" placeholder="Date from" :showButtonBar="true" />
						</div>
					</div>
					<div class="flex align-items-center" v-if="canEdit">
						<InputSwitch v-model="onlyActive" class="ml-4 mr-1" />
						<a class="mr-4" @click="onlyActive=!onlyActive">Only active</a>
					</div>
				</div>
				<div style="padding:10px;width:100%;padding-top:0px;">
					<DataView :value="filteredDocumentsList"
							  datakey="propertyServiceId"
							  :paginator="true"
							  :rows="10"
							  :scrollable="true"
							  stripedRows
							  class="p-datatable-sm"
							  :paginatorTemplate="paginatorTemplate"
							  :rowsPerPageOptions="[10,20,50,100,200]"
							  responsiveLayout="scroll"
							  :alwaysShowPaginator="false"
							  currentPageReportTemplate="Showing {first} to {last} of {totalRecords}">

						<template #header>
							<div class="col-12">
								<div class="grid flex-nowrap">
									<div class="col-fixed tablecol-icon"></div>
									<div class="col tablecol-name">Name</div>
									<div class="col-fixed hidden md:block tablecol-uploaded">Uploaded</div>
									<div class="col-fixed hidden xl:block tablecol-size">Size (KB)</div>
									<div class="col-fixed hidden xl:block tablecol-active" v-if="canEdit">Active</div>
								</div>
							</div>
						</template>

						<template #list="{data}">
							<div class="col-12">
								<div class="grid flex-nowrap hover-row">
									<div class="col-fixed tablecol-icon">
										<i class="pi pi-fw pi-file-pdf" v-if="data.fileName.toUpperCase().includes('.PDF')" />
										<i class="pi pi-fw pi-file-excel" v-else-if="data.fileName.toUpperCase().includes('.XLS')" />
										<i class="pi pi-fw pi-file" v-else />
									</div>
									<div class="col tablecol-name">
										<a @click="downloadDocument(data.documentId)">{{data.fileName}}</a>
									</div>
									<div class="col-fixed hidden md:block tablecol-uploaded">
										{{formattedDate(data.whenUploaded)}}
									</div>
									<div class="col-fixed hidden xl:block tablecol-size">
										{{data.size}}
									</div>
									<div class="col-fixed hidden xl:block tablecol-active" v-if="canEdit">
										<a @click="data.active =!data.active" v-tooltip.top="'Toggle active'">
											<i class="pi pi-circle-fill" v-if="data.active" />
											<i class="pi pi-circle" v-else />
										</a>
									</div>
									<div class="col-fixed text-right" v-if="canEdit">
										<a @click="data.whenDeleted = new Date();" v-tooltip.top="'Delete document'">
											<i class="pi pi-times" />
										</a>
									</div>
								</div>
							</div>
						</template>
					</DataView>
				</div>
			</TabPanel>
			<TabPanel v-if="canEdit">
				<template #header>
					<i class="pi pi-upload"></i>&nbsp;
					<span>Upload</span>
				</template>
				<FileUpload name="files[]" :customUpload="true" @uploader="fileUpload" :multiple="true" v-if="!uploading" :auto="false" accept=".pdf,.doc,.docx,.xls,.xlsx" @select="fileSelected">
					<template #empty>
						<p>Drag and drop files here to upload.</p>
					</template>
				</FileUpload>
				<div v-if="uploading">
					<span style="font-size: 2rem"><i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>&nbsp;Uploading...</span>
				</div>
			</TabPanel>
			<TabPanel v-if="canEdit">
				<template #header>
					<i class="pi pi-compass"></i>&nbsp;
					<span>Generate</span>
				</template>
				<div class="col-6 lg:col-3" v-for="doc in documentTypes.main" :key="doc.label">
					<Button style="width: 100%; border-radius:20px;" @click="toggleDocumentGenerationDialog(doc.templateName)"> <span>{{doc.label}}</span></Button>
				</div>
			</TabPanel>
		</TabView>
	</div>
	<!--Generate document dialog -->
	<DocumentGeneration :selectedDocumentTemplate="selectedDocumentTemplate"
						:propertyData="propertyData"
						:initialActiveTab="0"
						@updateActiveDocumentPanelTab="activeDocumentPanelTab = $event;"
						@updateShowDocumentGenerationComponent="showDocumentGenerationComponent = $event;"
						v-if="showDocumentGenerationComponent" />
</template>

<script lang="ts">
	import { CompassApi } from '@/api-client/CompassApiProxy';
	import { DocumentData, PropertyData, DownloadDocumentRequest } from '@/api-client/api-client';
	import { FileDataInterface } from '@/utils/CompassConstants';
	import Toast from '@/utils/Toast';
	import { format } from 'date-fns';
	import { FileUploadSelectEvent } from 'primevue/fileupload';
	import { Options, Vue } from 'vue-class-component';
	import { DocumentTypes, GetDocumentTypes } from '../utils/CompassConstants';
	import { DownloadFromFileResponse } from '../utils/DownloadFromFileResponse';
	import filterListBySearchString from '../utils/StringSearch';

	@Options({
		name: 'DocumentList',
		props: {
			propertyData: null,
			canEdit: {
				type: Boolean,
				default: false
			},
		}
	})
	export default class DocumentList extends Vue {

		// Data
		filterText: string = "";

		startDate: Date = new Date("2022-01-01T00:00:00.000Z");

		onlyActive: boolean = true;

		// From prop
		canEdit: boolean = false;

		// From prop
		propertyData: PropertyData = new PropertyData();

		documentDialog: boolean = false;

		selectedDocumentTemplate: string | null = null;

		showDocumentGenerationComponent: boolean = false;

		files = [];

		uploading: boolean = false;

		activeDocumentPanelTab: number = 0;

		documentTypes: DocumentTypes = GetDocumentTypes();

		created(): void {
			// Internal users can edit and toggle active/inactive
			this.onlyActive = this.canEdit ? false : true;
		}

		mounted(): void {
			this.startDate = this.propertyData?.whenFirstDocumentUploaded ?? this.startDate;
		}

		// Action
		click(): void {
			return;
		}

		fileSelected(event: FileUploadSelectEvent): void {
			(event.files as []).forEach((file: File) => {
				if (!this.checkValidFileNamePrefixAndToastError(file.name)) {
					return;
				}
			});
		}

		checkValidFileNamePrefixAndToastError(fileName: string): boolean {
			if (fileName.startsWith((this.propertyData.propertyId as number).toString()))
				return true;

			Toast.RemoveAllGroups();
			const errorMessage = `Documents for property ${this.propertyData.propertyId} must have filenames starting with '${this.propertyData.propertyId}', so '${fileName}' cannot be uploaded.`
			Toast.Error('Invalid file name', errorMessage);
			return false;
		}

		downloadDocument(id: number): void {
			const apiClient = CompassApi.documentApi(this.$store);

			const request = new DownloadDocumentRequest();
			request.documentId = id;
			request.inline = true;

			apiClient.downloadDocument(request)
				.then(response => {
					DownloadFromFileResponse(response);
				})
				.catch(async error => {
					let message = "Download failed.";

					// api exception with problem details as blob
					if (error.response instanceof Blob) {
						const text = await error.response.text();
						const problem = JSON.parse(text);
						message = problem.detail ?? problem.title ?? message;
					}

					Toast.Error("Download failed", message);
				});
		}

		// Computed
		get filteredDocumentsList(): DocumentData[] {
			let list = this.propertyData.documents != null ? this.propertyData.documents : [];

			list = list.filter(document => document.whenDeleted == null);

			if (this.onlyActive)
				list = list.filter(document => document.active === true);

			if (this.startDate)
				list = list.filter(document => document.whenUploaded ? document.whenUploaded >= this.startDate : false);

			list = filterListBySearchString(list, this.filterText, (document: DocumentData) => document.fileName + ' ' + document.referenceName + ' ' + document.whenUploaded);
			return list;
		}

		// Computed isDesktop
		get isDesktop(): boolean {
			return window.innerWidth >= 992;
		}

		// Computed to simplify paginator on mobile
		get paginatorTemplate(): string {
			return this.isDesktop ?
				"CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
				: "PrevPageLink NextPageLink"
		}

		formattedDate(date: Date | null) {
			if (!date) return;

			return format(date, 'dd MMM yyyy');
		}

		toggleDocumentGenerationDialog(docType: string) {
			this.selectedDocumentTemplate = docType;
			this.showDocumentGenerationComponent = true;
		}

		fileUpload(event: { files: FileList }) {
			const files: FileDataInterface[] = [];

			for (let i = 0; i < event.files.length; i++) {
				const file: File = event.files[i];
				if (!this.checkValidFileNamePrefixAndToastError(file.name)) {
					return;
				}
				const fileData = {
					"data": file,
					"fileName": file.name
				};
				files.push(fileData);
			}

			this.uploading = true;
			const apiClient = CompassApi.documentApi(this.$store);
			apiClient.uploadDocuments(this.propertyData.propertyId, files)
				.then(result => {
					this.uploading = false;

					// Set the updated documents list
					this.propertyData.documents = result;

					Toast.RemoveAllGroups();
					Toast.Success('Document upload', "The document upload(s) for property " + this.propertyData.propertyId + " succeeded.");

					this.activeDocumentPanelTab = 0;
				})
				.catch(error => {
					this.uploading = false;
					Toast.ApiError('Error uploading files', error);
				});


		}
	}
</script>

<style scoped lang="scss">
	.tablecol-icon {
		width: 28px;
		margin-top: 1px;
	}

	.tablecol-name {
		min-width: 0px;
		overflow-wrap: anywhere;
	}

	.tablecol-active {
		width: 100px;
		text-align: center;
	}

	.tablecol-size {
		width: 100px;
		text-align: right;
	}

	.tablecol-uploaded {
		width: 130px;
		text-align: right;
	}
</style>